"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DescriptionTextFromRowData = exports.DescriptionIconFromRowData = exports.DescriptionInheritedFromRowData = exports.DescriptionInfo = void 0;
const tslib_1 = require("tslib");
const widgets_1 = require("@tableau/widgets");
const catalog_icons_1 = require("@tableau/catalog-icons");
const catalog_messages_1 = require("@tableau/catalog-messages");
const react_1 = tslib_1.__importDefault(require("react"));
const catalog_core_1 = require("@tableau/catalog-core");
const VizClientFieldCardStyles_1 = require("../vizclient/VizClientFieldCardStyles");
/**
 * Displays a control if the field or column's description is inherited
 *
 * @param props The source column and/or field to be displayed
 * @returns An element representing the description info button
 */
function DescriptionInfo(props) {
    var _a, _b;
    const column = props.sourceColumn;
    const field = props.sourceField;
    const direction = (_a = props.direction) !== null && _a !== void 0 ? _a : 'vertical';
    const [isShown, setIsShown] = react_1.default.useState(false);
    const buttonRef = react_1.default.useRef(null);
    // for great accessibility, refocus the button when the dialog closes
    const closeDialog = () => {
        var _a;
        setIsShown(false);
        (_a = buttonRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    let descriptionTooltipContent = null;
    if (column && field) {
        descriptionTooltipContent = (react_1.default.createElement(VizClientFieldCardStyles_1.DescriptionTooltipText, null, (0, catalog_core_1.interpolateString)(catalog_messages_1.Messages.DescriptionInfoInheritedFromColumnThoughField({
            column: '[COLUMN]',
            table: '[TABLE]',
            field: '[FIELD]',
            datasource: '[DATASOURCE]',
        }), {
            COLUMN: react_1.default.createElement(ColumnLink, { key: 'column-link', column: column }),
            TABLE: react_1.default.createElement(TableLink, { key: 'table-link', table: column.table }),
            FIELD: react_1.default.createElement(FieldLink, { key: 'field-link', remoteField: field }),
            DATASOURCE: react_1.default.createElement(DatasourceLink, { key: 'datasource-link', remoteField: field }),
        })));
    }
    else if (field) {
        descriptionTooltipContent = (react_1.default.createElement(VizClientFieldCardStyles_1.DescriptionTooltipText, null, (0, catalog_core_1.interpolateString)(catalog_messages_1.Messages.DescriptionInfoInheritedFromField({
            field: '[FIELD]',
            datasource: '[DATASOURCE]',
        }), {
            FIELD: react_1.default.createElement(FieldLink, { key: 'field-link', remoteField: field }),
            DATASOURCE: react_1.default.createElement(DatasourceLink, { key: 'field-link', remoteField: field }),
        })));
    }
    else if (column) {
        descriptionTooltipContent = (react_1.default.createElement(VizClientFieldCardStyles_1.DescriptionTooltipText, null, (0, catalog_core_1.interpolateString)(catalog_messages_1.Messages.DescriptionInfoInheritedFromColumn({
            column: '[COLUMN]',
            table: '[TABLE]',
        }), {
            COLUMN: react_1.default.createElement(ColumnLink, { key: 'column-link', column: column }),
            TABLE: react_1.default.createElement(TableLink, { key: 'table-link', table: column.table }),
        })));
    }
    // If we have nothing to display in the tooltip, do not display an info button.
    if (descriptionTooltipContent === null) {
        return null;
    }
    const itemId = (_b = (column ? column.id : field === null || field === void 0 ? void 0 : field.id)) !== null && _b !== void 0 ? _b : ''; // Exactly one of column and field should be defined
    const anchorOrigin = direction === 'horizontal'
        ? {
            vertical: widgets_1.VerticalOriginOption.centerY,
            horizontal: widgets_1.HorizontalOriginOption.right,
        }
        : {
            vertical: widgets_1.VerticalOriginOption.top,
            horizontal: widgets_1.HorizontalOriginOption.centerX,
        };
    const descriptionIconStyle = {
        minWidth: 0,
        padding: '4px 8px 0px 8px',
    };
    return (react_1.default.createElement("div", { style: { display: 'inline-block' } },
        react_1.default.createElement("span", { className: VizClientFieldCardStyles_1.fieldCardDetailTitleIconClass },
            react_1.default.createElement(widgets_1.ButtonWidget, { buttonType: widgets_1.ButtonType.LowEmphasis, buttonRef: buttonRef, onClick: () => setIsShown(true), title: catalog_messages_1.Messages.DescriptionInfo(), testId: 'descriptionInfo-' + itemId, style: descriptionIconStyle },
                react_1.default.createElement(catalog_icons_1.SvgInfo, null))),
        isShown && (react_1.default.createElement(widgets_1.DialogWidget, { anchorEl: buttonRef.current, anchorOrigin: anchorOrigin, dialogWidth: 300, handleGlassClick: closeDialog, isGlassTransparent: true, isModal: true, onCloseRequest: closeDialog, positioningBehavior: widgets_1.DialogPositioningBehavior.Anchored, showCloseButton: false, testId: 'descriptionInfo-' + itemId + '-dialog', title: 'Description information', type: widgets_1.DialogType.Uber }, descriptionTooltipContent))));
}
exports.DescriptionInfo = DescriptionInfo;
function ColumnLink(props) {
    const siteUrls = (0, catalog_core_1.useSiteUrls)();
    return (react_1.default.createElement(widgets_1.TextLinkWidget, { testId: 'description-tooltip-column-link', href: maybeColumnUrl(props.column, siteUrls), target: '_blank' }, props.column.name));
}
function maybeColumnUrl(column, urls) {
    return column.table
        ? urls.catalogTableColumnUrl(column.table.id, column.id)
        : '';
}
function TableLink(props) {
    const siteUrls = (0, catalog_core_1.useSiteUrls)();
    return (react_1.default.createElement(widgets_1.TextLinkWidget, { testId: 'description-tooltip-table-link', href: maybeTableUrl(props.table, siteUrls), target: '_blank' }, props.table ? props.table.name : ''));
}
function maybeTableUrl(table, urls) {
    return table
        ? urls.catalogTableUrl(table.id)
        : '';
}
function FieldLink(props) {
    const siteUrls = (0, catalog_core_1.useSiteUrls)();
    return (react_1.default.createElement(widgets_1.TextLinkWidget, { testId: 'description-tooltip-field-link', href: maybeFieldUrl(props.remoteField, siteUrls), target: '_blank' }, props.remoteField.name));
}
function maybeFieldUrl(remoteField, urls) {
    return remoteField.datasource && remoteField.datasource.__typename === 'PublishedDatasource'
        ? urls.datasourceLineageFieldUrl(remoteField.datasource.vizportalUrlId, remoteField.id)
        : '';
}
function DatasourceLink(props) {
    var _a;
    const siteUrls = (0, catalog_core_1.useSiteUrls)();
    return (react_1.default.createElement(widgets_1.TextLinkWidget, { testId: 'description-tooltip-datasource-link', href: maybeDatasourceUrl(props.remoteField, siteUrls), target: '_blank' }, ((_a = props.remoteField.datasource) === null || _a === void 0 ? void 0 : _a.name) || ''));
}
function maybeDatasourceUrl(remoteField, urls) {
    return remoteField.datasource && remoteField.datasource.__typename === 'PublishedDatasource'
        ? urls.datasourceLineageUrl(remoteField.datasource.vizportalUrlId)
        : '';
}
function DescriptionInheritedFromRowData(rowData) {
    const descriptionInherited = rowData.descriptionInherited;
    // Gather the nearest defined, non-null descriptionInherited entry, if any.
    if (descriptionInherited == null || descriptionInherited.length === 0) {
        return null;
    }
    // Find an element with the lowest distance.
    const descriptionsInherited = descriptionInherited
        .filter((value) => (value === null || value === void 0 ? void 0 : value.attribute) === 'description');
    // Treat null- and undefined-distance items as very distant
    const maxDistance = 9999;
    const nearestElement = descriptionsInherited
        .sort((a, b) => {
        let aDistance = a === null || a === void 0 ? void 0 : a.distance;
        let bDistance = b === null || b === void 0 ? void 0 : b.distance;
        if (aDistance == null) {
            aDistance = maxDistance;
        }
        if (bDistance == null) {
            bDistance = maxDistance;
        }
        return aDistance - bDistance;
    })[0];
    // If no valid elements found, abort
    if (nearestElement === null) {
        return null;
    }
    // Find elements with the same distance. We should have exactly 1.
    const nearestElementsCount = descriptionsInherited
        .filter((value) => (value === null || value === void 0 ? void 0 : value.distance) === (nearestElement === null || nearestElement === void 0 ? void 0 : nearestElement.distance))
        .length;
    // If there are multiple elements tied as the closest, behave as if there were no inheritance
    if (nearestElementsCount !== 1) {
        return null;
    }
    return nearestElement;
}
exports.DescriptionInheritedFromRowData = DescriptionInheritedFromRowData;
function DescriptionIconFromRowData(rowData) {
    // We would only possibly display an icon if descriptionInherited is defined
    // and if a local description is not present.
    const descriptionInherited = DescriptionInheritedFromRowData(rowData);
    // Display no icon if a local description is present
    if (rowData.description != null) {
        return null;
    }
    if (descriptionInherited === null) {
        return null;
    }
    if (descriptionInherited.asset === null) {
        return null;
    }
    // We can have inheritance from a field or from a column
    if (descriptionInherited.asset.__typename === 'Column') {
        const inheritedAssetColumn = {
            id: descriptionInherited.asset.id,
            name: descriptionInherited.asset.name,
            table: descriptionInherited.asset.table,
        };
        return react_1.default.createElement(DescriptionInfo, { sourceColumn: inheritedAssetColumn });
    }
    if (descriptionInherited.asset.__typename.endsWith('Field')) {
        const inheritedAssetField = {
            id: descriptionInherited.asset.id,
            name: descriptionInherited.asset.name,
            datasource: descriptionInherited.asset.datasource,
            __typename: descriptionInherited.asset.__typename,
        };
        return react_1.default.createElement(DescriptionInfo, { sourceField: inheritedAssetField });
    }
    return null; // Should only happen if neither a column nor a field is the source of inheritance
}
exports.DescriptionIconFromRowData = DescriptionIconFromRowData;
function DescriptionTextFromRowData(rowData) {
    const description = rowData.description;
    const descriptionInherited = DescriptionInheritedFromRowData(rowData);
    // Only one of these should be defined. `description` depending on which query has been executed.
    if (description != null) {
        return description;
    }
    if (descriptionInherited !== null) {
        return descriptionInherited.value;
    }
    return null; // Should only happen if row data is malformed
}
exports.DescriptionTextFromRowData = DescriptionTextFromRowData;
//# sourceMappingURL=DescriptionInfo.js.map